import type { DocPageContent } from '../docs-content'

const page = {
  order: 6,
  slug: 'timeline',
  title: 'Edición básica de la línea de tiempo',
  description:
    'Pistas, agregar clips, selección, dividir y unir, eliminar, ajuste (snapping), marcadores, rangos, secuencias y zoom.',
  category: 'Edición principal',
  related: ['editing-tools', 'preview', 'media', 'export', 'keyboard-shortcuts'],
  sections: [
    {
      title: 'Pistas',
      blocks: [
        {
          kind: 'list',
          items: [
            'Las **pistas de video** contienen elementos visuales: video, imágenes, texto, formas, subtítulos y capas de ajuste.',
            'Las **pistas de audio** contienen clips de audio y el audio vinculado a archivos de video.',
            'Agrega pistas con **Agregar pista de video** o **Agregar pista de audio** cuando necesites más espacio.',
            'Los controles de pista incluyen **Bloquear**, **Solo**, **Activar/Desactivar** (visibilidad) y bloqueo de sincronización.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Los grupos de pistas tienen un solo nivel de profundidad y funcionan como encabezados: el silencio, la visibilidad y el bloqueo se transmiten del grupo a sus pistas.',
        },
      ],
    },
    {
      title: 'Agregar y organizar clips',
      blocks: [
        {
          kind: 'list',
          items: [
            'Arrastra un clip desde el panel de Medios hasta una pista de video o audio.',
            'Usa la herramienta **Selección** (`V`) para mover y reorganizar clips.',
            'La **selección vinculada** mantiene un clip de video y su audio moviéndose juntos; desvincúlalos para editar uno por separado.',
            'Desplaza un elemento seleccionado con `Shift+Flecha` (1px) o `Ctrl+Shift+Flecha` (10px).',
          ],
        },
      ],
    },
    {
      title: 'Dividir, unir y eliminar',
      blocks: [
        {
          kind: 'list',
          items: [
            'Divide en la posición del cursor de reproducción con `Ctrl+K`, o usa la herramienta **Cuchilla** (`C`) para cortar donde hagas clic.',
            'Une secciones adyacentes del mismo clip con `Shift+J`.',
            '**Eliminar** deja un espacio vacío; **Eliminación con ondulación** (`Ctrl+Delete`) elimina el clip y cierra el espacio.',
            'Usa **Cerrar todos los espacios** para juntar los clips y eliminar el espacio vacío en una pista.',
          ],
        },
      ],
    },
    {
      title: 'Ajuste (snapping), marcadores y rangos',
      blocks: [
        {
          kind: 'list',
          items: [
            'Activa el ajuste con `S` para que los clips se ajusten a cortes cercanos, marcadores y al cursor de reproducción.',
            'Agrega un marcador con `M` para notas y referencias de tiempo; salta entre marcadores con `[` y `]`.',
            'Define un punto de entrada (`I`) y un punto de salida (`O`) para establecer un rango de vista previa y exportación; bórralos con `Alt+X`.',
          ],
        },
      ],
    },
    {
      title: 'Secuencias (múltiples líneas de tiempo)',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Una **secuencia** es una línea de tiempo independiente con sus propias pistas, clips, marcadores y rango de entrada/salida. La barra de pestañas sobre la línea de tiempo muestra la línea de tiempo **Principal** más cada secuencia; haz clic en una pestaña para cambiar la que estás editando.',
        },
        {
          kind: 'list',
          items: [
            'Crea una secuencia con el botón **+** al final de la barra de pestañas; se abre como su propia pestaña.',
            'Cambia de secuencia haciendo clic en una pestaña; la pestaña **Principal** siempre aparece primero.',
            'Haz doble clic en una pestaña para **renombrarla**, o usa el menú de opciones de la pestaña (⋮) para **Renombrar**, **Cerrar pestaña** y **Eliminar secuencia**.',
            '**Cerrar pestaña** solo quita la pestaña: la secuencia sigue disponible en el panel de Medios y en cualquier clip que la referencie. **Eliminar secuencia** la elimina por completo (se puede deshacer), junto con cualquier clip que la referencie.',
            'Un **clip compuesto** se puede abrir como pestaña de secuencia: en el panel de Medios, haz clic derecho en un clip compuesto y elige **Abrir como pestaña** para editarlo como una línea de tiempo independiente.',
            'Suelta una secuencia en la línea de tiempo para anidarla como un clip (etiquetado como **Secuencia**); haz doble clic en ese clip para saltar a su pestaña.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Cada secuencia mantiene sus propios marcadores, rango de entrada/salida, vista e historial de deshacer; cambiar de pestaña nunca los mezcla.',
        },
      ],
    },
    {
      title: 'Zoom y deshacer',
      blocks: [
        {
          kind: 'list',
          items: [
            'Haz zoom con `Ctrl+=` y `Ctrl+-`, ajusta toda la edición a la vista con `\\`, y vuelve al 100% con `Shift+\\`.',
            'El zoom solo cambia la vista, nunca el tiempo de los clips.',
            'Deshacer con `Ctrl+Z` y rehacer con `Ctrl+Shift+Z`; la profundidad del historial de deshacer se puede configurar en Ajustes.',
          ],
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
